import type React from 'react';
import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@xing-com/button';
import { Card } from '@xing-com/card';
import {
  Gender,
  UserFlagsDisplayFlag,
} from '@xing-com/crate-common-graphql-types';
import { FLAG } from '@xing-com/crate-jobs-constants';
import type { HRContactData } from '@xing-com/crate-jobs-helpers';
import { useJobsNwtTracking } from '@xing-com/crate-jobs-tracking';
import { Flag } from '@xing-com/flag';
import { IconMessages } from '@xing-com/icons';
import { Link } from '@xing-com/link';
import { ProfileImage } from '@xing-com/profile-image';
import { ProfileInfo } from '@xing-com/profile-info';
import { spaceXL, xdlColorText } from '@xing-com/tokens';
import { BodyCopy, Headline, Meta } from '@xing-com/typography';

type HRContactCardProps = {
  hiringContact: HRContactData;
  jobId: string;
  className?: string;
};

const StyledCard = styled(Card)`
  padding: ${spaceXL};
`;

export const StyledLink = styled(Link)`
  color: ${xdlColorText};

  &:hover {
    text-decoration-thickness: from-font;
  }
`;

const trackingParams =
  'sc_o=jobs_profile_other_visit_contact_module&sc_o_PropActionOrigin=jobs_hr_contact';
const chatTrackingParams =
  'contextId=jobs_contact_hr&entryPoint=jobs_job_detail_recruiter';

export const HRContactCard: React.FC<HRContactCardProps> = ({
  hiringContact: {
    profileImage,
    gender,
    name,
    occupation,
    chatUrl,
    hrProfileUrl,
    userFlag,
    id,
  },
  jobId,
  className,
}) => {
  const { formatMessage } = useIntl();
  const linkLabel = formatMessage({
    id: 'JOBS_JOB_DETAILS_HR_CONTACT_MODULE_CARD_LINK_LABEL',
  });
  const trackNwt = useJobsNwtTracking();
  const hrProfileUrlWithTracking = `${hrProfileUrl}?${trackingParams}`;
  const trackHrContactVisit = useCallback(() => {
    trackNwt({
      event: 'opened',
      eventSchema: 'extended',
      page: 'jobs/detail',
      itemUrn: `surn:x-xing:users:user:${id}`,
      originalItemUrn: `surn:x-xing:jobs:posting:${jobId}`,
      element: 'profile_other_visit_contact_module',
      elementDetail: 'hr_contact',
    });
  }, [id, jobId, trackNwt]);

  return (
    <StyledCard
      className={className}
      to={hrProfileUrlWithTracking}
      data-testid="hr-contact-card"
      onClick={trackHrContactVisit}
      linkProps={{
        'aria-label': linkLabel,
      }}
    >
      <ProfileInfo
        alignment="vertical"
        profileImage={
          <StyledLink to={hrProfileUrlWithTracking} aria-label={linkLabel}>
            <ProfileImage
              profileName={name}
              size="medium"
              src={profileImage?.[0]?.url}
              type={
                gender === Gender.Male
                  ? 'male-user'
                  : Gender.Female
                    ? 'female-user'
                    : 'neutral-user'
              }
            />
          </StyledLink>
        }
        actions={
          <Button
            size="small"
            variant="secondary"
            icon={IconMessages}
            to={`${chatUrl}?${chatTrackingParams}`}
            onClick={(event) => {
              event.stopPropagation();
            }}
          >
            <FormattedMessage id="JOBS_JOB_DETAILS_MESSAGE_HR_CONTACT" />
          </Button>
        }
      >
        <Headline size="large">
          <StyledLink to={hrProfileUrlWithTracking}>{name}</StyledLink>
        </Headline>
        {userFlag === UserFlagsDisplayFlag.Premium ||
        userFlag === UserFlagsDisplayFlag.Insider ? (
          <Flag size="medium" variant={FLAG[userFlag]} />
        ) : null}
        {occupation?.subline ? (
          <BodyCopy size="medium">{occupation.subline}</BodyCopy>
        ) : null}
        {occupation?.headline ? (
          <Meta size="small">{occupation.headline}</Meta>
        ) : null}
      </ProfileInfo>
    </StyledCard>
  );
};
